/**
 * TaBiFoTo Admin API Client
 * ─────────────────────────────────────────────────────────────
 * Centralized HTTP client for all admin CMS + operations.
 *
 * Auth prep:
 *   - Call setAdminToken(token) to inject Bearer token for future requests.
 *   - Handles 401/403 by dispatching "admin:unauthorized" event.
 *
 * Base URL:
 *   - Reads VITE_ADMIN_API_URL from env. Falls back to "" (relative paths).
 */

const BASE_URL = (import.meta.env.VITE_ADMIN_API_URL as string | undefined) ?? "";

let _authToken: string | null = null;

export function setAdminToken(token: string | null): void {
  _authToken = token;
}

export function isApiConfigured(): boolean {
  return BASE_URL.length > 0;
}

// ─── Request core ─────────────────────────────────────────────────────────────

interface FetchOptions {
  method?: string;
  body?: string;
  headers?: Record<string, string>;
}

async function request<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers ?? {}),
  };
  if (_authToken) headers["Authorization"] = `Bearer ${_authToken}`;

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 401 || res.status === 403) {
    window.dispatchEvent(new CustomEvent("admin:unauthorized", { detail: { status: res.status } }));
    throw new ApiError(res.status, "Unauthorized");
  }
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, body || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

function get<T>(path: string): Promise<T> { return request<T>(path, { method: "GET" }); }
function post<T>(path: string, body: unknown): Promise<T> { return request<T>(path, { method: "POST", body: JSON.stringify(body) }); }
function patch<T>(path: string, body: unknown): Promise<T> { return request<T>(path, { method: "PATCH", body: JSON.stringify(body) }); }
function del<T>(path: string): Promise<T> { return request<T>(path, { method: "DELETE" }); }

// ─── Services ─────────────────────────────────────────────────────────────────

export const servicesApi = {
  list: () => get<unknown[]>("/api/admin/services"),
  create: (payload: unknown) => post<unknown>("/api/admin/services", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/services/${id}`, payload),
  deactivate: (id: string) => patch<unknown>(`/api/admin/services/${id}`, { is_active: false }),
};

// ─── Service Options ──────────────────────────────────────────────────────────

export const serviceOptionsApi = {
  list: (serviceId?: string) =>
    get<unknown[]>(serviceId ? `/api/admin/service-options?service_id=${serviceId}` : "/api/admin/service-options"),
  create: (payload: unknown) => post<unknown>("/api/admin/service-options", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/service-options/${id}`, payload),
  remove: (id: string) => del<void>(`/api/admin/service-options/${id}`),
};

// ─── Portfolio Items ──────────────────────────────────────────────────────────

export const portfolioApi = {
  list: () => get<unknown[]>("/api/admin/portfolio-items"),
  create: (payload: unknown) => post<unknown>("/api/admin/portfolio-items", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/portfolio-items/${id}`, payload),
  remove: (id: string) => del<void>(`/api/admin/portfolio-items/${id}`),
};

// ─── Homepage Sections ────────────────────────────────────────────────────────

export const homepageSectionsApi = {
  list: () => get<unknown[]>("/api/admin/homepage-sections"),
  create: (payload: unknown) => post<unknown>("/api/admin/homepage-sections", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/homepage-sections/${id}`, payload),
  remove: (id: string) => del<void>(`/api/admin/homepage-sections/${id}`),
};

// ─── Photographers — flat schema (no JSONB display_name) ──────────────────────

export const photographersApi = {
  list: () => get<unknown[]>("/api/admin/photographers"),
  create: (payload: unknown) => post<unknown>("/api/admin/photographers", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/photographers/${id}`, payload),
  deactivate: (id: string) => patch<unknown>(`/api/admin/photographers/${id}`, { is_active: false }),
};

// ─── Photographer Availability ────────────────────────────────────────────────

export const photographerAvailabilityApi = {
  list: (params?: { photographer_id?: string; city?: string; status?: string }) => {
    const qs = params ? "?" + new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][]
    ).toString() : "";
    return get<unknown[]>(`/api/admin/photographer-availability${qs}`);
  },
  create: (payload: unknown) => post<unknown>("/api/admin/photographer-availability", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/photographer-availability/${id}`, payload),
  remove: (id: string) => del<void>(`/api/admin/photographer-availability/${id}`),
};

// ─── Orders ──────────────────────────────────────────────────────────────────

export const ordersApi = {
  list: () => get<unknown[]>("/api/admin/orders"),
  create: (payload: unknown) => post<unknown>("/api/admin/orders", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/orders/${id}`, payload),
  remove: (id: string) => del<void>(`/api/admin/orders/${id}`),
};

// ─── Booking Confirmations ────────────────────────────────────────────────────

export const bookingConfirmationsApi = {
  list: () => get<unknown[]>("/api/admin/booking-confirmations"),
  create: (payload: unknown) => post<unknown>("/api/admin/booking-confirmations", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/booking-confirmations/${id}`, payload),
  remove: (id: string) => del<void>(`/api/admin/booking-confirmations/${id}`),
};

// ─── Legal Documents ──────────────────────────────────────────────────────────

export const legalDocumentsApi = {
  list: () => get<unknown[]>("/api/admin/legal-documents"),
  create: (payload: unknown) => post<unknown>("/api/admin/legal-documents", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/legal-documents/${id}`, payload),
  remove: (id: string) => del<void>(`/api/admin/legal-documents/${id}`),
};

// ─── Booking Cost Items ───────────────────────────────────────────────────────

export const bookingCostItemsApi = {
  list: (params?: { order_id?: string; cost_type?: string; cost_status?: string }) => {
    const qs = params ? "?" + new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][]
    ).toString() : "";
    return get<unknown[]>(`/api/admin/booking-cost-items${qs}`);
  },
  create: (payload: unknown) => post<unknown>("/api/admin/booking-cost-items", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/booking-cost-items/${id}`, payload),
  remove: (id: string) => del<void>(`/api/admin/booking-cost-items/${id}`),
};

// ─── Booking Notes ────────────────────────────────────────────────────────────

export const bookingNotesApi = {
  list: (params?: { order_id?: string; note_type?: string }) => {
    const qs = params ? "?" + new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][]
    ).toString() : "";
    return get<unknown[]>(`/api/admin/booking-notes${qs}`);
  },
  create: (payload: unknown) => post<unknown>("/api/admin/booking-notes", payload),
  remove: (id: string) => del<void>(`/api/admin/booking-notes/${id}`),
};

// ─── Order Cancellation Logs ──────────────────────────────────────────────────

export const orderCancellationLogsApi = {
  list: (params?: { order_id?: string }) => {
    const qs = params ? "?" + new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][]
    ).toString() : "";
    return get<unknown[]>(`/api/admin/order-cancellation-logs${qs}`);
  },
  create: (payload: unknown) => post<unknown>("/api/admin/order-cancellation-logs", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/order-cancellation-logs/${id}`, payload),
  remove: (id: string) => del<void>(`/api/admin/order-cancellation-logs/${id}`),
};

// ─── Order Reschedule Logs ────────────────────────────────────────────────────

export const orderRescheduleLogsApi = {
  list: (params?: { order_id?: string }) => {
    const qs = params ? "?" + new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][]
    ).toString() : "";
    return get<unknown[]>(`/api/admin/order-reschedule-logs${qs}`);
  },
  create: (payload: unknown) => post<unknown>("/api/admin/order-reschedule-logs", payload),
  update: (id: string, payload: unknown) => patch<unknown>(`/api/admin/order-reschedule-logs/${id}`, payload),
  remove: (id: string) => del<void>(`/api/admin/order-reschedule-logs/${id}`),
};

// ─── Me (auth bootstrap) ──────────────────────────────────────────────────────

/**
 * Shape of GET /api/admin/me from Codex backend.
 * Envelope: { data: { user: AdminUser, supported_locales: string[] } }
 */
export interface MeApiResponse {
  data: {
    user: {
      id: string;
      name?: string;
      email?: string;
      role?: string;
      [key: string]: unknown;
    };
    supported_locales?: string[];
  };
}

export const meApi = {
  /** GET /api/admin/me — validates the current Bearer token and returns the Codex envelope. */
  get: () => get<MeApiResponse>("/api/admin/me"),
};
